import { useCallback, useEffect } from 'react'
import { useDispatch } from 'react-redux'
import { trackInternalEvent } from '@rio/tracking'

import { useUser } from 'builder/hooks/useUser'
import { useTypedSelector } from 'builder/hooks/useTypedSelector'
import { useIsDashboardOpen } from 'builder/hooks/useIsDashboardOpen'
import { useFeaturesConfig } from 'builder/hooks/featureConfig/useFeaturesConfig'

import {
  selectors as renderingSelectors,
  actions as renderingActions,
} from 'builder/modules/rendering'
import { UserType } from 'builder/modules/user'
import { goToBillingPlanPage } from 'builder/utils/goToBillingPlanPage'

const MODAL_SHOWN_STORAGE_KEY = 'auto-apply-upsell-modal'

export const useAutoApplyUpsellModal = (initModal = false) => {
  const user = useUser()
  const dispatch = useDispatch()
  const { features } = useFeaturesConfig()
  const isDashboardOpen = useIsDashboardOpen()
  const showUpsellModal =
    !isDashboardOpen &&
    !window.location.pathname.includes('job-search') &&
    !window.location.pathname.includes('resume-distribution')

  const wasAutoApplyModalShown = localStorage.getItem(MODAL_SHOWN_STORAGE_KEY)
  const showAutoApplyUpsellModal = useTypedSelector(renderingSelectors.showAutoApplyUpsellModal)

  const handleCloseModal = () => {
    localStorage.setItem(MODAL_SHOWN_STORAGE_KEY, 'true')
    dispatch(renderingActions.toggleAutoApplyUpsellModal(false))
  }

  const handleOpenModal = () => {
    trackInternalEvent('show_auto_apply_upsell_modal', { label: 'auto_apply_click' })
    dispatch(renderingActions.toggleAutoApplyUpsellModal(true))
  }

  const handlePayment = () => {
    localStorage.setItem(MODAL_SHOWN_STORAGE_KEY, 'true')
    trackInternalEvent('click_auto_apply_upsell_modal')
    goToBillingPlanPage({
      referrerPage: 'auto-apply',
      upgradeURL: '/app/billing/payment',
      onSuccessRedirect: '/app/auto-apply/start',
      plan: '20_credits_autoapply',
    })
  }

  const showModalAfterPayment = useCallback(() => {
    if (
      initModal &&
      features?.autoApply &&
      user?.hasPremiumFeatures &&
      !user?.scopes?.autoApply &&
      !wasAutoApplyModalShown &&
      !user?.billingInfo.premiumEndsAt &&
      features?.autoApplyUpsellModal &&
      features?.autoApplyUpsellModal !== 'A' &&
      showUpsellModal
    ) {
      trackInternalEvent('show_auto_apply_upsell_modal')
      dispatch(renderingActions.toggleAutoApplyUpsellModal(true))
    }
  }, [
    dispatch,
    initModal,
    user?.billingInfo.premiumEndsAt,
    user?.hasPremiumFeatures,
    user?.scopes?.autoApply,
    wasAutoApplyModalShown,
    features?.autoApply,
    features?.autoApplyUpsellModal,
    showUpsellModal,
  ])

  useEffect(() => {
    if (showAutoApplyUpsellModal) return
    showModalAfterPayment()
  }, [showAutoApplyUpsellModal, showModalAfterPayment])

  return {
    isOpen: showAutoApplyUpsellModal,
    isTrialUser: user?.isTrialUser,
    isFreeUser: user?.billingInfo.userType === UserType.free,
    isPostPremiumUser: user?.billingInfo.userType === UserType.postPremium,
    planName: '20_credits_autoapply',
    handleOpenModal,
    handleCloseModal,
    handlePayment,
    showUpsellModal,
  }
}
